// Dashboard shared types

export type PeriodOption = '1D' | '7D' | '30D' | '90D' | 'custom';

export interface CustomDateRange {
  startDate: string;  // ISO date string (YYYY-MM-DD)
  endDate: string;
  comparisonOverride?: {
    value: number;
    unit: 'days' | 'weeks' | 'months';
  };
}

export type ChartKey = 'daily' | 'vsRandom' | 'vsFriends' | 'vsBot' | 'single';

export interface ChartDataPoint {
  label: string;
  date?: string;
  // Started counts
  daily: number;
  dailyCompleted?: number;
  dailyFailed?: number;
  vsRandom: number;
  vsFriends: number;
  vsBot: number;
  single: number;
  // Total VS games from DailyActivityLog (humans + ranked bots), or GameBoard for today
  vsGamesStarted?: number;
  vsGamesCompleted?: number;
  // Completed counts (solved, not expired)
  vsRandomCompleted?: number;
  vsFriendsCompleted?: number;
  vsBotCompleted?: number;
  singleCompleted?: number;
  // Online users for this period (for completion rate calculation)
  onlineUsers?: number;
}

export interface PeriodComparison {
  currentPeriodTotal: number;
  previousPeriodTotal: number;
  percentageChange: number;
}

export interface DailyChallengeParticipation {
  uniqueUsersAttempted: number;
  uniqueActiveUsers: number;
  participationRate: number;
  previousUniqueUsersAttempted: number;
  previousUniqueActiveUsers: number;
  previousParticipationRate: number;
  participationRateChange: number;
}

export interface GameHistoryResponse {
  success: boolean;
  dataPoints: ChartDataPoint[];
  totalGames: number;
  // Started games comparisons
  vsGamesComparison?: PeriodComparison;
  singleGamesComparison?: PeriodComparison;
  dailyChallengeComparison?: PeriodComparison;
  // Completed games comparisons
  vsGamesCompletedComparison?: PeriodComparison;
  singleGamesCompletedComparison?: PeriodComparison;
  // Daily challenge participation (unique users)
  dailyChallengeParticipation?: DailyChallengeParticipation;
}

export interface TopPerformerInfo {
  userId?: string;
  username?: string;
  mode?: string;
  wins?: number;
  totalPlays?: number;
  rankName?: string;
}

export interface DailyChallengeStat {
  label: string;
  value: string;
  change: string;
  positive: boolean;
  neutral?: boolean;
}

export interface Performer {
  name: string;
  mode: string;
  wins?: number;
  totalPlays?: number;
  userId?: string;
  rankName?: string;
}

export interface ActiveUserInfo {
  userId: string;
  username: string;
  rankName: string;
  lastActivityTime: string | null;
  vsGames: number;
  botGames: number;
  singleGames: number;
  dailyChallengeGames: number;
  totalGamesPlayed: number;
}

export interface ActiveUsersResponse {
  success: boolean;
  users: ActiveUserInfo[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export type CardKey = 'chart' | 'dailyActiveUsers' | 'vsGames' | 'singleGames' | 'dailyChallenge' | 'completedVsGames' | 'completedVsBots' | 'topPerformers';

export interface PeriodState {
  chart: string;
  dailyActiveUsers: PeriodOption;
  vsGames: PeriodOption;
  singleGames: PeriodOption;
  dailyChallenge: PeriodOption;
  completedVsGames: PeriodOption;
  completedVsBots: PeriodOption;
  topPerformers: PeriodOption;
  customRanges: Partial<Record<CardKey, CustomDateRange>>;
}

export const defaultPeriodState: PeriodState = {
  chart: '30D',
  dailyActiveUsers: '1D',
  vsGames: '1D',
  singleGames: '1D',
  dailyChallenge: '1D',
  completedVsGames: '7D',
  completedVsBots: '7D',
  topPerformers: '1D',
  customRanges: {},
};
